/**
 * Custo efetivo de uma resposta de cotação, para a tabela comparativa (RN-046).
 *
 * O ponto que a tabela existe para revelar: o menor preço unitário raramente é
 * o menor custo. Um fornecedor R$ 1,40 mais barato por peça que exige o dobro
 * da quantidade mínima e cobra frete pode sair mais caro na conta final.
 *
 * Duas regras que este módulo aplica sem exceção:
 *
 * - Campo não informado é NULO, nunca zero (RN-044). Um fornecedor que não
 *   disse o frete não cobra frete zero — o custo dele é DESCONHECIDO, não
 *   menor. O resultado sai como incompleto, dizendo o que falta.
 *
 * - A quantidade que entra na conta é a que será REALMENTE comprada,
 *   respeitando a quantidade mínima do fornecedor.
 */

import { arredondar, porQuantidade, porUnidade } from './inteiros.js';

export interface RespostaComparavel {
  precoUnitarioCents: number | null;
  quantidadeMinimaMilli: number | null;
  quantidadeDisponivelMilli: number | null;
  freteCents: number | null;
  impostosCents: number | null;
}

export type CustoEfetivo =
  | {
      completo: true;
      quantidadeEfetivaMilli: number;
      /** true quando a mínima do fornecedor forçou comprar mais que o desejado. */
      minimoForcouAumento: boolean;
      produtosCents: number;
      custoTotalCents: number;
      custoUnitarioEfetivoCents: number;
      /** Quantidade disponível insuficiente para o que se pretende comprar. */
      estoqueInsuficiente: boolean;
    }
  | { completo: false; faltando: string[] };

const OBRIGATORIOS: ReadonlyArray<readonly [keyof RespostaComparavel, string]> = [
  ['precoUnitarioCents', 'preço unitário'],
  ['freteCents', 'frete'],
  ['impostosCents', 'impostos'],
];

export function custoEfetivo(
  r: RespostaComparavel,
  quantidadeDesejadaMilli: number,
): CustoEfetivo {
  const faltando = OBRIGATORIOS.filter(([c]) => r[c] === null || r[c] === undefined).map(
    ([, rotulo]) => rotulo,
  );
  if (faltando.length > 0) return { completo: false, faltando };
  if (quantidadeDesejadaMilli <= 0) throw new Error('quantidade desejada deve ser maior que zero');

  const minima = r.quantidadeMinimaMilli ?? 0;
  const quantidadeEfetivaMilli = Math.max(quantidadeDesejadaMilli, minima);
  const minimoForcouAumento = minima > quantidadeDesejadaMilli;

  const produtosCents = porQuantidade(r.precoUnitarioCents!, quantidadeEfetivaMilli);
  const custoTotalCents = produtosCents + r.freteCents! + r.impostosCents!;

  return {
    completo: true,
    quantidadeEfetivaMilli,
    minimoForcouAumento,
    produtosCents,
    custoTotalCents,
    custoUnitarioEfetivoCents: porUnidade(custoTotalCents, quantidadeEfetivaMilli),
    estoqueInsuficiente:
      r.quantidadeDisponivelMilli !== null && r.quantidadeDisponivelMilli < quantidadeEfetivaMilli,
  };
}

export interface LinhaComparativo<T> {
  origem: T;
  custo: CustoEfetivo;
}

/**
 * Ordena o comparativo pelo custo unitário efetivo, com as linhas incompletas
 * no fim.
 *
 * Incompleto vai para o fim de propósito: ele não é "o mais caro", é o
 * desconhecido — e colocá-lo no meio da ordenação sugeriria uma posição que o
 * dado não sustenta.
 */
export function ordenarPorCustoEfetivo<T>(linhas: LinhaComparativo<T>[]): LinhaComparativo<T>[] {
  return [...linhas].sort((a, b) => {
    if (a.custo.completo && b.custo.completo) {
      return a.custo.custoUnitarioEfetivoCents - b.custo.custoUnitarioEfetivoCents;
    }
    if (a.custo.completo) return -1;
    if (b.custo.completo) return 1;
    return 0;
  });
}

/** Diferença percentual em relação à melhor linha completa, em pontos-base. */
export function diferencaParaMelhorBps<T>(linhas: LinhaComparativo<T>[]): (number | null)[] {
  const completos = linhas.filter((l) => l.custo.completo);
  if (completos.length === 0) return linhas.map(() => null);
  const melhor = Math.min(
    ...completos.map((l) => (l.custo as Extract<CustoEfetivo, { completo: true }>).custoUnitarioEfetivoCents),
  );
  if (melhor === 0) return linhas.map(() => null);

  return linhas.map((l) =>
    l.custo.completo
      ? arredondar((l.custo.custoUnitarioEfetivoCents - melhor) * 10_000, melhor)
      : null,
  );
}
